import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import "./DocsPage.css";

const DOCS = [
  {
    category: "Getting Started",
    title: "Overview",
    path: "/docs/getting-started.md",
    description: "Navigation, dashboard, and app basics.",
  },
  {
    category: "Indicators & Metrics",
    title: "Funding Payment Aggregation",
    path: "/docs/funding-aggregation.md",
    description: "How the FUNDING column and history aggregates are computed.",
  },
  {
    category: "Indicators & Metrics",
    title: "Margins (Kev M%)",
    path: "/docs/margins-kev.md",
    description: "Maintenance margin and Kev M% calculation per exchange.",
  },
  {
    category: "Indicators & Metrics",
    title: "Hedging Analysis",
    path: "/docs/hedging.md",
    description: "Hedge ratio, net exposure, and status (fully/partially hedged).",
  },
  {
    category: "Reference",
    title: "API Endpoints",
    path: "/docs/api-endpoints.md",
    description: "Backend API reference for positions, history, and admin.",
  },
];

const groupByCategory = (docs) =>
  docs.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {});

export default function DocsPage() {
  const [activeDoc, setActiveDoc] = useState(DOCS[0]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const categories = useMemo(() => groupByCategory(DOCS), []);

  useEffect(() => {
    let isActive = true;
    const fetchDoc = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}${activeDoc.path}`);
        if (!response.ok) {
          throw new Error(`Failed to load ${activeDoc.title}`);
        }
        const text = await response.text();
        if (isActive) {
          setContent(text);
        }
      } catch (err) {
        if (isActive) {
          setError(err.message || "Failed to load documentation");
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchDoc();
    return () => {
      isActive = false;
    };
  }, [activeDoc]);

  return (
    <div className="docs-page">
      <aside className="docs-sidebar">
        <div className="docs-sidebar-header">Documentation</div>
        {Object.entries(categories).map(([category, docs]) => (
          <div key={category} className="docs-category">
            <div className="docs-category-title">{category}</div>
            {docs.map((doc) => (
              <button
                key={doc.path}
                className={`docs-link ${activeDoc.path === doc.path ? "active" : ""}`}
                onClick={() => setActiveDoc(doc)}
                type="button"
              >
                <div className="docs-link-title">{doc.title}</div>
                <div className="docs-link-desc">{doc.description}</div>
              </button>
            ))}
          </div>
        ))}
      </aside>

      <section className="docs-content">
        {loading && <div className="docs-loading">Loading...</div>}
        {error && <div className="docs-error">⚠️ {error}</div>}
        {!loading && !error && (
          <ReactMarkdown>{content}</ReactMarkdown>
        )}
      </section>
    </div>
  );
}
